import type { PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { useMutation } from "@octopusdeploy/octopus-react-client";
import type { MachineConnectionStatus, MachinePolicyResource, MachineResource, Repository } from "@octopusdeploy/octopus-server-client";
import { EndpointsHelper, HostingEnvironment, isDeploymentTarget, isWorkerMachine } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { cloneDeep } from "lodash";
import React, { useState } from "react";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
import { BundledComponents } from "./BundledComponents";
import { CalamariDetails } from "./CalamariDetails";
import { CommunicationLogs } from "./CommunicationLogs";
import { MachineHealthStatusCallout } from "./MachineHealthStatusCallout";
import { TentacleDetails } from "./TentacleDetails";
interface MachineConnectivityPageProps {
    loaderData: MachineConnectivityPageLoaderData;
    itemDescription: string;
}
export interface MachineConnectivityPageLoaderData {
    machine: MachineResource;
    connectionStatus: MachineConnectionStatus;
    isRunningInHACluster: boolean;
    machinePolicy: MachinePolicyResource;
}
export async function machineConnectivityPageLoader(repository: Repository, machineId: string, machineType: "deploymentTarget" | "worker"): Promise<MachineConnectivityPageLoaderData> {
    const machineRepository = machineType === "deploymentTarget" ? repository.Machines : repository.Workers;
    const machine = await machineRepository.get(machineId);
    const connectionStatus = await machineRepository.getConnectionStatus(machine);
    const machinePolicy = await repository.MachinePolicies.get(machine.MachinePolicyId);
    const licensePromise = repository.Licenses.getCurrentStatus();
    const serverNodes = await repository.OctopusServerNodes.all();
    const isSelfHosted = (await licensePromise).HostingEnvironment === HostingEnvironment.SelfHosted;
    const isRunningInHACluster = isSelfHosted && serverNodes.length > 1;
    return {
        machine,
        connectionStatus,
        isRunningInHACluster,
        machinePolicy,
    };
}
export function MachineConnectivityPage({ loaderData, itemDescription }: MachineConnectivityPageProps) {
    const { navigate } = useSpaceAwareNavigation();
    const [machine, setMachine] = useState(loaderData.machine);
    const { execute: createHealthCheckTask, isExecuting: isCreatingHealthCheckTask } = useMutation({
        name: "Create health check task",
        action: (repo) => repo.Tasks.createHealthCheckTaskForMachine(machine),
        afterComplete: async (_, createdTask) => navigate(links.taskPage.generateUrl({ taskId: createdTask.Id })),
    });
    const { execute: toggleVersionLock, isExecuting: isTogglingLock } = useMutation({
        name: "Toggle tentacle version lock",
        action: async (repo, isLocked: boolean) => {
            const updatedMachine = cloneDeep(machine);
            if (EndpointsHelper.isTentacle(updatedMachine.Endpoint)) {
                updatedMachine.Endpoint.TentacleVersionDetails.UpgradeLocked = isLocked;
            }
            else if (EndpointsHelper.isKubernetesTentacle(updatedMachine.Endpoint)) {
                updatedMachine.Endpoint.UpgradeLocked = isLocked;
            }
            if (isDeploymentTarget(updatedMachine)) {
                return await repo.Machines.modify(updatedMachine);
            }
            if (isWorkerMachine(updatedMachine)) {
                return await repo.Workers.modify(updatedMachine);
            }
            throw new Error("Invalid machine type");
        },
        afterComplete: async (_, savedMachine) => setMachine(savedMachine),
    });
    const checkHealthPageAction: PrimaryPageAction = {
        type: "button",
        label: "Check health",
        onClick: createHealthCheckTask,
        disabled: isCreatingHealthCheckTask,
    };
    return (<PageContent header={{ title: "Connectivity", primaryAction: checkHealthPageAction }}>
            <MachineHealthStatusCallout communicationStyle={machine.Endpoint.CommunicationStyle} healthStatus={loaderData.connectionStatus.Status} lastChecked={String(loaderData.connectionStatus.LastChecked)} itemDescription={itemDescription}/>
            <CalamariDetails machine={machine}/>
            <TentacleDetails machine={machine} machinePolicy={loaderData.machinePolicy} onToggleVersionLock={toggleVersionLock} isTogglingLock={isTogglingLock}/>
            <BundledComponents machine={machine} isRunningInHACluster={loaderData.isRunningInHACluster}/>
            <CommunicationLogs connectionStatus={loaderData.connectionStatus}/>
        </PageContent>);
}
